import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLazyQuery, useMutation } from '@apollo/client';
import { SAVE_CLIENT } from '@src/graphql/queries/client';
import { useEmployers } from '@src/hooks/useEmployers';
import { PaymentRequestAnchorDateType } from '@src/types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CheckboxInput } from '@guildeducationinc/recess';
import { CLIENTS, FileUpload, Form, GET_URL, useAlert, } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
import styles from './EditClientModal.module.scss';
export const EditClientModal = ({ client, onCancel, onSave }) => {
    const { showAlert } = useAlert();
    const [saveClient] = useMutation(SAVE_CLIENT, {
        refetchQueries: [CLIENTS],
    });
    const [s3Key, setS3Key] = useState(null);
    const [file, setFile] = useState(null);
    const [getUrl] = useLazyQuery(GET_URL);
    const [logoUrl, setLogoUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [enableSegment, setEnableSegment] = useState(!(client.clientConfig?.extraData?.disableSegment ?? false));
    const [enableWeglot, setEnableWeglot] = useState(!(client.clientConfig?.extraData?.disableWeglot ?? false));
    const [disableAI, setDisableAI] = useState(!(client.clientConfig?.extraData?.enableAI ?? true)); // this is saved as enableAI in the database
    const [enableSierra, setEnableSierra] = useState(client.clientConfig?.extraData?.enableSierra ?? false);
    const [collectReceiptDates, setCollectReceiptDates] = useState(client.clientConfig?.extraData?.collectReceiptDatesForAllTransactions ?? false);
    // Initialize client with fundingAnchorDateType and budgetAnchorDateType for form
    const clientData = useMemo(() => ({
        ...client,
        fundingAnchorDateType: client.clientConfig?.extraData?.fundingAnchorDateType ??
            PaymentRequestAnchorDateType.CREATED_DATE,
        budgetAnchorDateType: client.clientConfig?.extraData?.budgetAnchorDateType ??
            PaymentRequestAnchorDateType.CREATED_DATE,
    }), [client]);
    useEffect(() => {
        if (client.logoS3Key) {
            (async () => {
                const { data } = await getUrl({ variables: { input: { s3Key: client.logoS3Key } } });
                if (data) {
                    setLogoUrl(data.getUrl);
                }
            })();
        }
    }, [client.logoS3Key]);
    const { employers } = useEmployers();
    const ClientFields = useMemo(() => {
        const employerOptions = employers
            .map(e => ({ value: e.id.toString(), label: e.name }))
            .sort((a, b) => a.label.localeCompare(b.label));
        const fundingDateTypeOptions = [
            { value: PaymentRequestAnchorDateType.CREATED_DATE, label: 'Created Date' },
            {
                value: PaymentRequestAnchorDateType.SUBMITTED_DATE,
                label: 'Submitted Date',
            },
            {
                value: PaymentRequestAnchorDateType.DOCUMENT_SUBMISSION_DATE,
                label: 'Document Submission Date',
            },
            {
                value: PaymentRequestAnchorDateType.TERM_START_DATE,
                label: 'Term Start Date',
            },
            {
                value: PaymentRequestAnchorDateType.TERM_END_DATE,
                label: 'Term End Date',
            },
            {
                value: PaymentRequestAnchorDateType.APPROVED_DATE,
                label: 'Approved Date',
            },
        ];
        const budgetDateTypeOptions = [
            { value: PaymentRequestAnchorDateType.CREATED_DATE, label: 'Created Date' },
            // {
            //   value: PaymentRequestAnchorDateType.SUBMITTED_DATE,
            //   label: 'Submitted Date',
            // },
            // {
            //   value: PaymentRequestAnchorDateType.DOCUMENT_SUBMISSION_DATE,
            //   label: 'Document Submission Date',
            // },
            {
                value: PaymentRequestAnchorDateType.TERM_START_DATE,
                label: 'Term Start Date',
            },
            {
                value: PaymentRequestAnchorDateType.TERM_END_DATE,
                label: 'Term End Date',
            },
            // {
            //   value: PaymentRequestAnchorDateType.APPROVED_DATE,
            //   label: 'Approved Date',
            // },
        ];
        return [
            {
                name: 'code',
                label: 'Code',
                type: 'text',
                helpText: 'Used for the client URL. Lowercase letters, numbers, hyphen and underscore only',
            },
            { name: 'name', label: 'Name', type: 'text' },
            {
                name: 'employerId',
                label: 'Employer',
                type: 'lookup',
                options: employerOptions,
            },
            {
                name: 'fundingAnchorDateType',
                label: 'Cap Year Assignment Date',
                type: 'lookup',
                options: fundingDateTypeOptions,
            },
            {
                name: 'budgetAnchorDateType',
                label: 'Budget Year Assignment Date',
                type: 'lookup',
                options: budgetDateTypeOptions,
            },
            {
                name: 'isParent',
                label: 'Parent Client?',
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
                infoText: 'Parent client help text',
            },
            {
                name: 'isActive',
                label: 'Active?',
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
                disabled: client.code === 'guild', // guild client is always active
            },
        ];
    }, [employers]);
    const handleSave = async (clientData) => {
        try {
            const result = await saveClient({
                variables: {
                    input: {
                        id: clientData.id || null,
                        code: clientData.code,
                        name: clientData.name,
                        employerId: Number(clientData.employerId),
                        logoS3Key: clientData.logoS3Key || null,
                        isActive: clientData.isActive,
                        s3Key,
                        disableSegment: !enableSegment,
                        disableWeglot: !enableWeglot,
                        enableAi: !disableAI, // this is saved as enableAI in the database, but graphql field needs to be enableAi
                        enableSierra,
                        collectReceiptDatesForAllTransactions: collectReceiptDates,
                        fundingAnchorDateType: clientData.fundingAnchorDateType,
                        budgetAnchorDateType: clientData.budgetAnchorDateType,
                        isParent: clientData.isParent,
                    },
                },
            });
            if (result.data.saveClient.success) {
                showAlert('Client saved');
                onSave();
            }
            else {
                return result.data.saveClient;
            }
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            showAlert(e.message, 'error');
        }
    };
    const handleValidate = (clientData) => {
        // make sure all the fields are filled out
        const errors = {};
        if (!clientData.code) {
            errors.code = 'Code is required';
        }
        if (!clientData.name) {
            errors.name = 'Name is required';
        }
        if (!clientData.employerId) {
            errors.employerId = 'Employer is required';
        }
        if (clientData.code) {
            if (!/^[a-z0-9-_]+$/.test(clientData.code)) {
                errors.code = 'Code must be lowercase letters, numbers, hyphen and underscore only';
            }
        }
        return errors;
    };
    const handleUpload = (field, setDraft) => async (s3Key, file) => {
        setUploading(true);
        try {
            if (s3Key && file) {
                if (field === 'logoS3Key') {
                    setDraft(prev => ({ ...prev, logoS3Key: s3Key }));
                    const { data } = await getUrl({ variables: { input: { s3Key } } });
                    setLogoUrl(data.getUrl);
                }
                else {
                    setS3Key(s3Key);
                }
                setFile(file);
            }
        }
        finally {
            setUploading(false);
        }
    };
    return (_jsx(Form, { obj: clientData, fields: ClientFields, onValidate: handleValidate, onSave: handleSave, onCancel: onCancel, modal: true, title: client.id ? 'Edit Client' : 'Add Client', children: ({ setDraft }) => (_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx("div", { children: _jsx(CheckboxInput, { label: 'Enable Segment?', name: 'enableSegment', checked: enableSegment, onChange: e => setEnableSegment(e.target.checked) }) }), _jsx("div", { children: _jsx(CheckboxInput, { label: 'Enable Weglot?', name: 'enableWeglot', checked: enableWeglot, onChange: e => setEnableWeglot(e.target.checked) }) }), _jsx("div", { children: _jsx(CheckboxInput, { label: 'Disable AI?', name: 'disableAI', checked: disableAI, onChange: e => setDisableAI(e.target.checked) }) }), _jsx("div", { children: _jsx(CheckboxInput, { label: 'Enable Sierra?', name: 'enableSierra', checked: enableSierra, onChange: e => setEnableSierra(e.target.checked) }) }), _jsx("div", { children: _jsx(CheckboxInput, { label: 'Collect Receipt Dates For All Transactions?', name: 'collectReceiptDates', checked: collectReceiptDates, onChange: e => setCollectReceiptDates(e.target.checked) }) }), !client.id && (_jsxs("div", { children: [file && _jsx("div", { children: file.name }), _jsx(FileUpload, { label: '', hideLabel: true, fileType: 'json', s3KeyPrefix: 'client-configs', onUploaded: handleUpload('s3Key', setDraft), buttonLabel: 'Upload JSON File', disabled: uploading, buttonSize: 'small', tag: 'upload_client_config' })] })), _jsxs("div", { className: styles.logoContainer, children: [_jsx(FileUpload, { label: '', hideLabel: true, fileType: 'image', s3KeyPrefix: 'client-configs', onUploaded: handleUpload('logoS3Key', setDraft), buttonLabel: 'Upload Logo', disabled: uploading, buttonSize: 'small', tag: 'upload_client_logo' }), logoUrl && _jsx("img", { className: styles.logo, src: logoUrl, alt: '' })] })] })) }));
};
